/**
 * Посилання переходу між областями (WCAG 2.4.1; keyboard.md, розд. 5, п. 9): перший `Tab` на
 * екрані, до верхньої панелі. Окремої клавіші немає: `F6` лишається браузеру.
 *
 * Набір будується з областей, які реально є на екрані: «До бічної панелі» — лише коли панель
 * відкрита. Навігація — одна з двох (рейка або нижня панель телефона), яку зараз видно.
 * Перехід переносить фокус на контейнер області (`tabIndex={-1}`, кільце — `.main:focus-visible`
 * у app.css), тож наступний `Tab` іде вже всередині області.
 */
import type { MouseEvent } from 'react';
import { shell as t } from '../i18n/uk/shell.ts';
import { BOTTOM_NAV_ID } from './BottomBar.tsx';
import { NAV_ID } from './SideNav.tsx';
import { ASIDE_ID } from './TodayAside.tsx';

export const MAIN_ID = 'main';

/**
 * Область видно (а не `display: none` за адаптивом): фокус на прихованому елементі не стає.
 * Обчислений стиль, а не `getClientRects`: у jsdom розкладки немає, а стилі адаптиву — на самій області.
 */
const isShown = (el: HTMLElement) => {
  const style = getComputedStyle(el);
  return style.display !== 'none' && style.visibility !== 'hidden';
};

/** Перший видимий з кандидатів: рейка навігації або нижня панель телефона. */
export function focusArea(ids: readonly string[]): boolean {
  for (const id of ids) {
    const el = document.getElementById(id);
    if (!el || !isShown(el)) continue;
    el.focus();
    // Липка шапка не перекриває область (2.4.11); `nearest` не гойдає екран, коли вона вже видна.
    el.scrollIntoView?.({ block: 'nearest' });
    return true;
  }
  return false;
}

interface SkipLinkProps {
  href: string;
  ids: readonly string[];
  children: string;
}

function SkipLink({ href, ids, children }: SkipLinkProps) {
  const go = (e: MouseEvent<HTMLAnchorElement>) => {
    // Без зміни адреси: хеш-посилання зламало б історію роутера.
    e.preventDefault();
    focusArea(ids);
  };
  return (
    <a className="skip-link" href={href} onClick={go}>
      {children}
    </a>
  );
}

export function SkipLinks({ hasNav, hasAside }: { hasNav: boolean; hasAside: boolean }) {
  return (
    <>
      <SkipLink href={`#${MAIN_ID}`} ids={[MAIN_ID]}>
        {t.skipToContent}
      </SkipLink>
      {hasNav && (
        <SkipLink href={`#${NAV_ID}`} ids={[NAV_ID, BOTTOM_NAV_ID]}>
          {t.skipToNav}
        </SkipLink>
      )}
      {hasAside && (
        <SkipLink href={`#${ASIDE_ID}`} ids={[ASIDE_ID]}>
          {t.skipToAside}
        </SkipLink>
      )}
    </>
  );
}
